import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import { toast } from 'react-toastify';
import { EventSectionSaleProductReport } from '@/model/EventSectionSaleProductReport';
import { EventMachineSaleProductReport } from '@/model/EventMachineSaleProductReport';
import { ReportControllerProps } from '../../types';
import { SaleProductSectionsContent } from './ui';

interface StateProps {
  reportController: ReportControllerProps;
  setState: (state: States) => void;
  onExportPDF?: (sections: EventSectionSaleProductReport[]) => void;
}

export const SaleProductSections: React.FC<StateProps> = ({
  reportController,
  setState,
  onExportPDF,
}): JSX.Element => {
  const { eventId, singleEvent } = reportController;
  const [sections, setSections] = useState<EventSectionSaleProductReport[]>([]);
  const [allSections, setAllSections] = useState<EventSectionSaleProductReport[]>([]);

  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [productName, setProductName] = useState<string>('');
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [posOptions, setPosOptions] = useState<{ label: string; value: string }[]>([]);

  const handleFetchPosList = async (): Promise<void> => {
    try {
      const { data } = await api.get<EventMachineSaleProductReport[]>(
        `event/report/${eventId}/sale-product/machine?singleEvent=${singleEvent}`,
      );
      if (data && data.length > 0) {
        const uniqueSerials = Array.from(
          new Set(data.map(m => m.serialNumber).filter(Boolean)),
        ).sort();
        setPosOptions(uniqueSerials.map(s => ({ label: `POS: ${s}`, value: s })));
      }
    } catch {
      // Caso ocorra erro, os seriais ainda podem ser extraídos de allSections
    }
  };

  const handleGetSaleProducts = async (
    filterSections = selectedSectionIds,
    filterProduct = productName,
    filterSerial = serialNumber,
  ): Promise<void> => {
    try {
      setState(States.loading);

      const params = new URLSearchParams();
      params.append('singleEvent', String(singleEvent));
      if (filterSections.length === 1) {
        params.append('sectionId', filterSections[0]);
      }
      if (filterProduct.trim()) {
        params.append('productName', filterProduct.trim());
      }
      if (filterSerial.trim()) {
        params.append('serialNumber', filterSerial.trim());
      }

      const { data } = await api.get<any>(
        `event/report/${eventId}/sale-product/section?${params.toString()}`,
      );
      if (data) {
        let dataArray: EventSectionSaleProductReport[] = [];
        if (Array.isArray(data)) {
          dataArray = data;
        } else if (data) {
          dataArray = [data];
        }

        if (
          allSections.length === 0 ||
          (dataArray.length > allSections.length && filterSections.length === 0)
        ) {
          setAllSections(dataArray);
        }

        let filtered = dataArray;
        if (filterSections.length === 1 && dataArray.length === 1) {
          filtered = dataArray;
        } else if (filterSections.length > 0) {
          filtered = dataArray.filter(sec => {
            const secId = String(sec.id || (sec as any).sectionId || '');
            return filterSections.some(id => String(id) === secId);
          });
        }

        setSections(filtered);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleClearFilter = (): void => {
    setSelectedSectionIds([]);
    setProductName('');
    setSerialNumber('');
    handleGetSaleProducts([], '', '');
  };

  useEffect(() => {
    if (posOptions.length === 0) {
      const source = allSections.length > 0 ? allSections : sections;
      const serials = new Set<string>();
      source.forEach(sec => {
        sec.machines?.forEach(m => {
          if (m.serialNumber) serials.add(m.serialNumber);
        });
      });
      if (serials.size > 0) {
        setPosOptions(
          Array.from(serials)
            .sort()
            .map(s => ({ label: `POS: ${s}`, value: s })),
        );
      }
    }
  }, [allSections, sections]);

  useEffect(() => {
    handleGetSaleProducts();
    handleFetchPosList();
  }, [singleEvent]);

  return (
    <SaleProductSectionsContent
      sections={sections}
      allSections={allSections}
      selectedSectionIds={selectedSectionIds}
      productName={productName}
      serialNumber={serialNumber}
      posOptions={posOptions}
      onChangeSectionIds={setSelectedSectionIds}
      onChangeProductName={setProductName}
      onChangeSerialNumber={setSerialNumber}
      onFilter={() => handleGetSaleProducts(selectedSectionIds, productName, serialNumber)}
      onClearFilter={handleClearFilter}
      onExportPDF={
        onExportPDF
          ? (dataToExport?: EventSectionSaleProductReport[]) =>
              onExportPDF(dataToExport || sections)
          : undefined
      }
    />
  );
};
